import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, MapPin, ArrowRight } from 'lucide-react';
import demos from '../../data/demos';

function DemosGallery() {
  return (
    <section id="demos" className="relative py-24 bg-surface/30 border-y border-border">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <p className="text-sm text-accent-2 font-medium mb-3 uppercase tracking-wider">Sample sites</p>
            <h2 className="font-display font-bold text-5xl md:text-6xl text-text mb-4 leading-tight">
              See what your{' '}
              <span className="gradient-text">new website</span>{' '}
              could look like.
            </h2>
            <p className="text-text-muted text-lg">
              A few full working demos — click any one to walk through it. Yours will be custom, but this is the level of polish you'll get.
            </p>
          </div>
          <Link
            to="/demos"
            className="group flex-shrink-0 inline-flex items-center gap-2 text-sm font-semibold text-text-muted hover:text-text transition-colors"
          >
            See all 6 industries
            <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {demos.slice(0, 3).map((d, i) => (
            <motion.div
              key={d.slug}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '0px 0px 300px 0px' }}
              transition={{ duration: 0.3, delay: (i % 3) * 0.04 }}
            >
              <Link
                to={`/demos/${d.slug}`}
                className="group block glass rounded-2xl overflow-hidden hover:border-accent/40 transition-all hover:-translate-y-1 duration-300"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-surface-2">
                  <img
                    src={d.cover}
                    alt={d.name}
                    width="1600"
                    height="900"
                    loading={i < 3 ? 'eager' : 'lazy'}
                    decoding="async"
                    fetchpriority={i < 3 ? 'high' : 'low'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-bg/90 via-bg/20 to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="glass px-3 py-1 rounded-full text-xs text-text">
                      {d.industry}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gradient-brand flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowUpRight size={16} className="text-white" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display font-semibold text-lg text-text mb-1">{d.name}</h3>
                  <p className="text-sm text-text-muted mb-3 line-clamp-2">{d.tagline}</p>
                  <div className="flex items-center gap-1.5 text-xs text-text-muted">
                    <MapPin size={12} />
                    <span>{d.location}</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/demos"
            className="group bg-gradient-brand text-white px-7 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 hover:shadow-glow transition-shadow"
          >
            Browse all sample sites
            <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default DemosGallery;
